import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { RefreshCw, Download, Filter, X, Search, CheckCircle2, AlertTriangle, ShieldCheck, ClipboardList, Inbox } from 'lucide-react';
import { format, parseISO, isWithinInterval, startOfWeek, endOfWeek } from 'date-fns';
import { Modal } from '../../components/ui/UI';
import './ObservationRegisterPage.css';

const BU_KEY = 'hse_register_business_unit';
const WEEK_KEY = 'hse_register_week_key';
const SAFE = 'Safe Behavior';

// Columns: filterable ones get a checklist popover in the header
const COLUMNS = [
  { key: 'sn', label: 'S/N', cls: 'col-sn' },
  { key: 'fullName', label: 'Observer' },
  { key: 'email', label: 'Email', cls: 'col-muted' },
  { key: 'businessUnit', label: 'Business Unit', filter: true },
  { key: 'department', label: 'Department', filter: true },
  { key: 'designation', label: 'Designation', filter: true },
  { key: 'observationDate', label: 'Date' },
  { key: 'shift', label: 'Shift', filter: true },
  { key: 'observationLocation', label: 'Location', filter: true },
  { key: 'behaviorType', label: 'Type', filter: true },
  { key: 'behaviorCategory', label: 'Category', filter: true },
  { key: 'antecedent', label: 'Possible Reason', filter: true },
  { key: 'behaviorDescription', label: 'Description', long: true },
  { key: 'otherBehaviorsObserved', label: 'Other Behaviors', long: true },
  { key: 'action', label: '' },
];

const fmtDate = (d) => (d ? format(new Date(d), 'dd MMM yyyy') : '—');

function FilterPopover({ column, options, selected, onToggle, onClear, onClose }) {
  const [q, setQ] = useState('');
  const shown = options.filter((o) => String(o).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fp" role="dialog" aria-label={`Filter ${column.label}`}>
      <div className="fp-search">
        <Search size={12} />
        <input autoFocus placeholder={`Search ${column.label.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="fp-list">
        {shown.length === 0 && <div className="fp-empty">No matches</div>}
        {shown.map((v) => (
          <label key={v} className="fp-opt">
            <input type="checkbox" checked={selected.includes(v)} onChange={() => onToggle(column.key, v)} />
            <span>{v}</span>
          </label>
        ))}
      </div>
      <div className="fp-foot">
        <button className="link-btn" onClick={() => onClear(column.key)} disabled={!selected.length}>Clear</button>
        <button className="link-btn link-btn--strong" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

export default function ObservationRegisterPage() {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [closeId, setCloseId] = useState(null);
  const [closing, setClosing] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(() => localStorage.getItem(WEEK_KEY) || '');
  const [selectedBU, setSelectedBU] = useState(() => localStorage.getItem(BU_KEY) || '');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/observations?limit=10000&closed=false');
      setObservations(data.observations.filter((o) => o.closedAction !== true));
    } catch {
      toast.error('Could not load observations. Check your connection and refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { localStorage.setItem(BU_KEY, selectedBU); }, [selectedBU]);
  useEffect(() => { localStorage.setItem(WEEK_KEY, selectedWeek); }, [selectedWeek]);

  const matchesFilters = useCallback(
    (obs) => Object.entries(filters).every(([col, vals]) => !vals.length || vals.includes(obs[col])),
    [filters]
  );

  const uniqueValues = useCallback(
    (col) => [...new Set(observations.map((o) => o[col]).filter(Boolean))].sort(),
    [observations]
  );

  const buOptions = useMemo(() => uniqueValues('businessUnit'), [uniqueValues]);

  const currentWeek = useMemo(() => {
    const t = new Date();
    const weekStart = startOfWeek(t, { weekStartsOn: 1 });
    return { key: weekStart.toISOString(), weekStart, weekEnd: endOfWeek(t, { weekStartsOn: 1 }) };
  }, []);

  const weekOptions = useMemo(() => {
    const map = new Map();
    observations.forEach((o) => {
      if (!o.observationDate) return;
      const d = parseISO(o.observationDate);
      const weekStart = startOfWeek(d, { weekStartsOn: 1 });
      const key = weekStart.toISOString();
      if (!map.has(key)) map.set(key, { key, weekStart, weekEnd: endOfWeek(d, { weekStartsOn: 1 }) });
    });
    if (!map.has(currentWeek.key)) map.set(currentWeek.key, currentWeek);
    return [...map.values()].sort((a, b) => b.weekStart - a.weekStart);
  }, [observations, currentWeek]);

  // Keep stored selections valid
  useEffect(() => {
    if (selectedWeek === 'all') return;
    if (!selectedWeek || !weekOptions.some((w) => w.key === selectedWeek)) setSelectedWeek(currentWeek.key);
  }, [weekOptions, selectedWeek, currentWeek]);

  useEffect(() => {
    if (!loading && selectedBU && buOptions.length && !buOptions.includes(selectedBU)) setSelectedBU('');
  }, [buOptions, selectedBU, loading]);

  // Close popover on outside click / Escape
  useEffect(() => {
    if (!openFilter) return;
    const onDown = (e) => { if (!e.target.closest('.fp-wrap')) setOpenFilter(null); };
    const onKey = (e) => e.key === 'Escape' && setOpenFilter(null);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openFilter]);

  const filtered = useMemo(() => {
    const week = selectedWeek && selectedWeek !== 'all' ? weekOptions.find((w) => w.key === selectedWeek) : null;
    const q = search.trim().toLowerCase();
    return observations
      .filter((o) => {
        if (selectedBU && o.businessUnit !== selectedBU) return false;
        if (!matchesFilters(o)) return false;
        if (week) {
          if (!o.observationDate) return false;
          if (!isWithinInterval(parseISO(o.observationDate), { start: week.weekStart, end: week.weekEnd })) return false;
        }
        if (q) {
          const hay = [o.fullName, o.email, o.department, o.designation, o.observationLocation, o.behaviorCategory, o.antecedent, o.behaviorDescription, o.otherBehaviorsObserved].join(' ').toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.sn || 0) - (b.sn || 0));
  }, [observations, selectedBU, selectedWeek, weekOptions, matchesFilters, search]);

  const stats = useMemo(() => {
    const safe = filtered.filter((o) => o.behaviorType === SAFE).length;
    return { total: filtered.length, safe, risk: filtered.length - safe, pct: filtered.length ? Math.round((safe / filtered.length) * 100) : 0 };
  }, [filtered]);

  const activeChips = Object.entries(filters).flatMap(([col, vals]) => vals.map((v) => ({ col, v })));
  const hasFilters = activeChips.length > 0 || Boolean(search) || Boolean(selectedBU) || (selectedWeek && selectedWeek !== 'all' && selectedWeek !== currentWeek.key);

  const toggleValue = (col, v) =>
    setFilters((p) => ({ ...p, [col]: p[col]?.includes(v) ? p[col].filter((x) => x !== v) : [...(p[col] || []), v] }));
  const clearColumn = (col) => setFilters((p) => ({ ...p, [col]: [] }));
  const clearAll = () => { setFilters({}); setSearch(''); setSelectedBU(''); setSelectedWeek('all'); setOpenFilter(null); };
  const toggleExpand = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const confirmClose = async () => {
    if (!closeId) return;
    setClosing(true);
    try {
      await api.patch(`/observations/${closeId}/close`, { closed: true });
      toast.success('Action closed and moved to Closed Actions');
      setCloseId(null);
      navigate('/admin/closed-actions');
    } catch {
      toast.error('Could not close this action. Try again.');
    } finally {
      setClosing(false);
    }
  };

  const exportCsv = () => {
    if (!filtered.length) return toast.error('There are no rows to export');
    const headers = ['S/N', 'Full Name', 'Email Address', 'Business Unit', 'Department', 'Designation', 'Observation Date', 'Shift', 'Observation Location', 'Behavior Type', 'Behavior Category', 'Antecedent/Possible Reason', 'Behavior Description', 'Other Behaviors Observed'];
    const rows = filtered.map((o) => [o.sn, o.fullName, o.email, o.businessUnit, o.department, o.designation, o.observationDate ? format(new Date(o.observationDate), 'dd/MM/yyyy') : '', o.shift, o.observationLocation, o.behaviorType, o.behaviorCategory, o.antecedent, o.behaviorDescription, o.otherBehaviorsObserved]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Observation_Register_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} observation${filtered.length === 1 ? '' : 's'}`);
  };

  const renderCell = (o, col, idx) => {
    switch (col.key) {
      case 'sn': return o.sn || idx + 1;
      case 'observationDate': return fmtDate(o.observationDate);
      case 'behaviorType':
        return <span className={`badge ${o.behaviorType === SAFE ? 'badge--safe' : 'badge--risk'}`}>{o.behaviorType === SAFE ? 'Safe' : 'At-risk'}</span>;
      case 'behaviorDescription':
      case 'otherBehaviorsObserved': {
        const text = o[col.key];
        if (!text) return '—';
        const key = `${o._id}:${col.key}`;
        const open = expanded.has(key);
        return (
          <div>
            <div className={open ? 'longtext' : 'longtext longtext--clamp'}>{text}</div>
            {text.length > 110 && (
              <button className="link-btn" onClick={() => toggleExpand(key)}>{open ? 'Show less' : 'Show more'}</button>
            )}
          </div>
        );
      }
      case 'action':
        return <button className="btn-close" onClick={() => setCloseId(o._id)}><CheckCircle2 size={14} /> Close</button>;
      default: return o[col.key] || '—';
    }
  };

  const statCards = [
    { icon: ClipboardList, label: 'Open observations', value: stats.total, tone: 'ink' },
    { icon: ShieldCheck, label: 'Safe behaviors', value: stats.safe, tone: 'safe' },
    { icon: AlertTriangle, label: 'At-risk behaviors', value: stats.risk, tone: 'risk' },
    { icon: CheckCircle2, label: 'Safe share', value: `${stats.pct}%`, tone: 'ink' },
  ];

  return (
    <div className="reg">
      <header className="reg-head">
        <div>
          <h1>Observation Register</h1>
          <p>Behavior-Based Safety &amp; Visible Felt Leadership · Snacks, Dairies, Swan · Rev 01</p>
        </div>
        <div className="reg-actions">
          <button className="btn btn--ghost" onClick={fetchData} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn--solid" onClick={exportCsv}><Download size={15} /> Export CSV</button>
        </div>
      </header>

      <section className="stats" aria-label="Summary of current view">
        {statCards.map(({ icon: Icon, label, value, tone }) => (
          <div key={label} className={`stat stat--${tone}`}>
            <span className="stat-ic"><Icon size={18} /></span>
            <div>
              <div className="stat-val">{loading ? '–' : value}</div>
              <div className="stat-lbl">{label}</div>
            </div>
          </div>
        ))}
      </section>

      <section className="bar">
        <div className="search">
          <Search size={15} />
          <input placeholder="Search names, locations, descriptions…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search observations" />
          {search && <button onClick={() => setSearch('')} aria-label="Clear search"><X size={14} /></button>}
        </div>
        <label className="sel">
          <span>Business unit</span>
          <select value={selectedBU} onChange={(e) => setSelectedBU(e.target.value)}>
            <option value="">All units</option>
            {buOptions.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </label>
        <label className="sel">
          <span>Week</span>
          <select value={selectedWeek || 'all'} onChange={(e) => setSelectedWeek(e.target.value)}>
            <option value="all">All records</option>
            {weekOptions.map((w) => (
              <option key={w.key} value={w.key}>
                {format(w.weekStart, 'dd MMM')} – {format(w.weekEnd, 'dd MMM yyyy')}{w.key === currentWeek.key ? ' (this week)' : ''}
              </option>
            ))}
          </select>
        </label>
      </section>

      {(activeChips.length > 0 || hasFilters) && (
        <div className="chips" aria-live="polite">
          <span className="chips-count">Showing {filtered.length} of {observations.length}</span>
          {activeChips.map(({ col, v }) => (
            <button key={col + v} className="chip" onClick={() => toggleValue(col, v)} title="Remove filter">
              {COLUMNS.find((c) => c.key === col)?.label}: {v} <X size={12} />
            </button>
          ))}
          <button className="link-btn link-btn--strong" onClick={clearAll}>Clear all filters</button>
        </div>
      )}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} className={c.cls || ''} scope="col">
                  <div className="th">
                    <span>{c.label}</span>
                    {c.filter && (
                      <div className="fp-wrap">
                        <button
                          className={`fbtn ${filters[c.key]?.length ? 'fbtn--on' : ''}`}
                          onClick={() => setOpenFilter(openFilter === c.key ? null : c.key)}
                          aria-label={`Filter ${c.label}`}
                          aria-expanded={openFilter === c.key}
                        >
                          <Filter size={12} />
                          {filters[c.key]?.length > 0 && <b>{filters[c.key].length}</b>}
                        </button>
                        {openFilter === c.key && (
                          <FilterPopover column={c} options={uniqueValues(c.key)} selected={filters[c.key] || []} onToggle={toggleValue} onClear={clearColumn} onClose={() => setOpenFilter(null)} />
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 7 }).map((_, r) => (
                <tr key={r}>{COLUMNS.map((c) => <td key={c.key}><span className="skel" /></td>)}</tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <div className="empty">
                    <Inbox size={34} />
                    <h3>{hasFilters ? 'No observations match these filters' : 'No open observations'}</h3>
                    <p>{hasFilters ? 'Try a different week or remove a filter.' : 'New observations will appear here once submitted.'}</p>
                    {hasFilters && <button className="btn btn--solid" onClick={clearAll}>Clear all filters</button>}
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((o, i) => (
                <tr key={o._id}>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={`${c.cls || ''} ${c.long ? 'col-long' : ''}`}>{renderCell(o, c, i)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!closeId} onClose={() => !closing && setCloseId(null)} title="Close this action?">
        <div className="confirm">
          <p>It will move to the Closed Actions register and disappear from this list.</p>
          <div className="confirm-btns">
            <button className="btn btn--ghost" onClick={() => setCloseId(null)} disabled={closing}>Keep open</button>
            <button className="btn btn--solid" onClick={confirmClose} disabled={closing}>{closing ? 'Closing…' : 'Close action'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}